import React, { useState, useEffect } from 'react'
import './SegmentationExplorerDescription.css'
import { PLOTS_COLORS } from './Colors'
import { GeneralInformationCard } from './GeneralInformationCard'
import { SegmentDistributionChart } from './SegmentDistributionChart'
import { NonIdealState, Spinner } from '@blueprintjs/core'

/**
 * SegmentationExplorerDescription component renders information about a segmentation along with charts.
 * @param {Object} props - Component props.
 * @param {Object} props.segmentation - The segmentation data containing fields like algo, id, size, silhouette_score, inputs, seed, variable, and is_imported.
 * @param {boolean} props.isLoading - Whether the segmentation data is currently loading.
 */
export function SegmentationExplorerDescription({ segmentation, isLoading }) {
  const [useWeightedData, setUseWeightedData] = useState(false)
  const [userHasInteracted, setUserHasInteracted] = useState(false)

  const hasWeightedData =
    segmentation?.variable?.weight_sums &&
    segmentation.variable.weight_sums.length > 0 &&
    segmentation.variable.total_weight_sum > 0

  // Auto-enable weighted data ONLY on first load or if user hasn't interacted yet
  useEffect(() => {
    if (segmentation && !userHasInteracted) {
      setUseWeightedData(hasWeightedData)
    }
  }, [segmentation?.id, hasWeightedData, segmentation, userHasInteracted])

  // Handler for switch toggle - marks user interaction
  const handleWeightedDataToggle = () => {
    setUserHasInteracted(true)
    setUseWeightedData(prev => !prev)
  }

  if (!segmentation) {
    return <div>No segmentations .</div>
  }

  const {
    id,
    algo,
    size,
    seed,
    nmf_runs,
    snmf_balance_tolerance,
    variable,
    inputs,
    silhouette_score,
    is_imported,
  } = segmentation

  // Calculate segment distribution based on selected data type
  const calculateSegmentDistribution = () => {
    const {
      unique_segments,
      counts,
      total_count,
      weight_sums,
      total_weight_sum,
    } = variable

    // Validate that we have the necessary data
    if (!unique_segments || !counts || !total_count) {
      return []
    }

    if (useWeightedData && weight_sums && total_weight_sum > 0) {
      // Calculate weighted distribution per segment
      return unique_segments.map((segment, index) => ({
        name: `Segment ${segment}`,
        value: weight_sums[index],
        percentage: ((weight_sums[index] / total_weight_sum) * 100).toFixed(2),
        isWeighted: true,
      }))
    } else {
      // Calculate normal distribution (unweighted) per segment
      return unique_segments.map((segment, index) => ({
        name: `Segment ${segment}`,
        value: counts[index],
        percentage: ((counts[index] / total_count) * 100).toFixed(2),
        isWeighted: false,
      }))
    }
  }

  const segmentDistribution = calculateSegmentDistribution()

  return (
    <div className="segmentation-explorer-description-div">
      <GeneralInformationCard
        algo={algo}
        size={size}
        silhouette_score={silhouette_score}
        inputs={inputs}
        seed={seed}
        is_imported={is_imported}
        id={id}
        nmf_runs={nmf_runs}
        snmf_balance_tolerance={snmf_balance_tolerance}
        isLoading={isLoading}
      />

      <SegmentDistributionChart
        segmentDistribution={segmentDistribution}
        COLORS={PLOTS_COLORS}
        isWeighted={useWeightedData}
        useWeightedData={useWeightedData}
        setUseWeightedData={handleWeightedDataToggle}
        hasWeightedData={hasWeightedData}
        isLoading={isLoading}
      />
    </div>
  )
}
